import { useAppSelector } from '@/app/hooks'
import { Button, BorderButton } from '@/components/atoms/button'
import { SelectBox } from '@/components/atoms/select-box'
import { DocumentSlotList } from '@/components/molecules/checkIn/document-slot-list'
import { RetakeFailureDisplay } from '@/components/molecules/checkIn/identity-verify-error-dialog'
import { IdentityVerifyCaptureLayer } from '@/components/organisms/checkIn/identity-verify-capture-layer'
import { IdentificationFormProps } from '@/components/organisms/checkIn/identification-form-props'
import { DocumentCameraPreset, DocumentSlotDefinition, useDocumentSlots } from '@/hooks/use-document-slots'
import { FormLayout } from '@/components/template/form-layout'
import { spacerTopStyle } from '@/styles/common'
import { formButtonStyle } from '@/styles/form'
import { css } from '@emotion/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

type JapaneseDocumentType = 'license' | 'mynumber' | 'residence'

type FaceSide = 'front' | 'back'

// `staffRegisterLabel` は登録APIにそのまま渡り、据置端末と同じ列に入る。据置端末
// （aipass_hotel の use-self-check-in-state.ts:249-250）と同じ文言にしてある
const RequiredSidesByDocumentType: Record<JapaneseDocumentType, { side: FaceSide; staffRegisterLabel: string }[]> = {
  license: [
    { side: 'front', staffRegisterLabel: '免許証表面' },
    { side: 'back', staffRegisterLabel: '免許証裏面' },
  ],
  mynumber: [{ side: 'front', staffRegisterLabel: 'マイナンバーカード表面' }],
  residence: [
    { side: 'front', staffRegisterLabel: '在留カード表面' },
    { side: 'back', staffRegisterLabel: '在留カード裏面' },
  ],
}

const SampleImageByDocumentTypeAndSide: Record<JapaneseDocumentType, Partial<Record<FaceSide, string>>> = {
  license: {
    front: require('@/static/images/checkIn/drivers-license-front-sample.svg').default,
    back: require('@/static/images/checkIn/drivers-license-back-sample.svg').default,
  },
  mynumber: {
    front: require('@/static/images/checkIn/my-number-card-front-sample.svg').default,
  },
  residence: {
    front: require('@/static/images/checkIn/residence-card-front-sample.svg').default,
    back: require('@/static/images/checkIn/residence-card-back-sample.svg').default,
  },
}

const slotId = (documentType: JapaneseDocumentType, side: FaceSide) => `japanese-${documentType}-${side}`

const everyPossibleSlotId = Object.entries(RequiredSidesByDocumentType).flatMap(([documentType, sides]) =>
  sides.map(({ side }) => slotId(documentType as JapaneseDocumentType, side)),
)

export const JapaneseResidentForm: React.FC<IdentificationFormProps> = ({ hotelId, requiredIdentifyVerification, isSkippable }) => {
  const { t } = useTranslation()
  const images = useAppSelector(state => state.checkIn.identification.images)

  const findDocumentTypeWithImages = (): JapaneseDocumentType | undefined =>
    (Object.keys(RequiredSidesByDocumentType) as JapaneseDocumentType[]).find(type =>
      RequiredSidesByDocumentType[type].some(({ side }) => images[slotId(type, side)]),
    )

  const [documentType, setDocumentType] = useState<JapaneseDocumentType>(() => findDocumentTypeWithImages() ?? 'license')

  const documentTypeOptions: { value: JapaneseDocumentType; label: string }[] = [
    { value: 'license', label: t("Driver's License") },
    { value: 'mynumber', label: t('My Number Card') },
    { value: 'residence', label: t('Residence Card') },
  ]

  const documentTypeLabel = (type: JapaneseDocumentType) => documentTypeOptions.find(option => option.value === type)?.label ?? ''
  const sideLabel = (side: FaceSide) => (side === 'front' ? t('Front side') : t('Back side'))

  const requiredSides = RequiredSidesByDocumentType[documentType]

  const visibleSlotDefinitions: DocumentSlotDefinition[] = requiredSides.map(({ side, staffRegisterLabel }) => ({
    slot: {
      slotId: slotId(documentType, side),
      idType: side === 'front' ? 'drivingLicenseFront' : 'drivingLicenseBack',
      staffRegisterLabel,
      order: side === 'front' ? 0 : 1,
    },
    label: `${documentTypeLabel(documentType)}｜${sideLabel(side)}`,
    sampleImageUrl: SampleImageByDocumentTypeAndSide[documentType][side],
    cameraInstruction: t('Please align the {{side}} of your {{document}} within the frame and take a photo', {
      document: documentTypeLabel(documentType),
      side: sideLabel(side),
    }),
  }))

  const retakeDisplay: RetakeFailureDisplay = {
    heading: `${t('ID card not detected.')}\n${t('Please take another photo.')}`,
  }

  const cameraPreset: DocumentCameraPreset = {
    guide: { aspectRatio: '342 / 214', widthPercent: 88 },
    shutter: { variant: 'button', label: t('Capture') },
  }

  const { slotViews, captureLayer, isReadyToProceed, next, skip } = useDocumentSlots({
    hotelId,
    requiredIdentifyVerification,
    visibleSlotDefinitions,
    everyPossibleSlotId,
    cameraPreset,
    retakeDisplay,
  })

  const handleDocumentTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setDocumentType(event.target.value as JapaneseDocumentType)
  }

  return (
    <div>
      <FormLayout>
        <h2 css={headingStyle}>{t('Please register your identification for verification purposes')}</h2>
        <p css={noticeStyle}>
          {t(
            'As this facility operates unstaffed, we verify the identity of our guests as required by law. Uploaded images are used only for identity verification and are properly managed by this facility and the system provider entrusted by this facility.',
          )}
        </p>

        <SelectBox
          itemCss={spacerTopStyle}
          name="japaneseDocumentType"
          label={t('Identification type')}
          value={documentType}
          options={documentTypeOptions}
          arrowDefaultSelect={false}
          onChange={handleDocumentTypeChange}
        />

        <DocumentSlotList slots={slotViews} />
      </FormLayout>

      <Button buttonCss={[spacerTopStyle, formButtonStyle]} text={t('Next')} disabled={!isReadyToProceed} onClick={next} />
      {isSkippable && <BorderButton buttonCss={[spacerTopStyle, formButtonStyle]} text={t('Skip & Present at Check-in')} onClick={skip} />}

      <IdentityVerifyCaptureLayer {...captureLayer} />
    </div>
  )
}

const headingStyle = css({
  fontSize: '16px',
  fontWeight: 'bold',
  textAlign: 'center',
})

const noticeStyle = css({
  marginTop: '0.75rem',
  fontSize: '12px',
  lineHeight: '1.5',
})
